import { useState, useCallback } from 'react'
import verifyIsLoginActive from 'util/verifyIsLoginActive'
import openExternalLink from 'util/LinkToShowCase'
import axios from 'shared/http'
import { sanitizeDocument } from 'util/document'

const getDocumentType = (participante) => {
  if (!participante) return ''

  const typePerson = participante.typePerson
  if (
    typePerson === 'Física' ||
    typePerson === 'Physics' ||
    typePerson === 'Fisica'
  ) {
    return 'CPF'
  } else if (
    typePerson === 'Jurídica' ||
    typePerson === 'Legal' ||
    typePerson === 'Juridica'
  ) {
    return 'CNPJ'
  }

  const documento = participante.document || ''
  const documentoLimpo = sanitizeDocument(documento)
  if (documentoLimpo.length === 11) {
    return 'CPF'
  } else if (documentoLimpo.length === 14) {
    return 'CNPJ'
  }

  return ''
}

export const useDocumentValidation = () => {
  const [showModal, setShowModal] = useState(false)
  const [isValidating, setIsValidating] = useState(false)
  const [pendingRedirect, setPendingRedirect] = useState(false)
  const [participantes, setParticipantes] = useState(null)
  const [hasMultipleAccountTypes, setHasMultipleAccountTypes] = useState(false)
  const [hasNegativeBalance, setHasNegativeBalance] = useState(false)

  const checkDocumentValidation = useCallback(async () => {
    // Abre o modal imediatamente com loading para dar feedback visual ao usuário
    setShowModal(true)
    setPendingRedirect(true)
    setIsValidating(true)

    try {
      verifyIsLoginActive()

      const url = '/participants/v2/info'
      const response = await axios.get(url)

      const participantesList = Array.isArray(response) ? response : [response]

      // Processa os participantes de forma mais eficiente
      let participanteFisica = null
      let participanteJuridica = null

      for (const p of participantesList) {
        const typePerson = p.typePerson
        if (
          !participanteFisica &&
          (typePerson === 'Física' ||
            typePerson === 'Physics' ||
            typePerson === 'Fisica')
        ) {
          participanteFisica = p
        }
        if (
          !participanteJuridica &&
          (typePerson === 'Jurídica' ||
            typePerson === 'Legal' ||
            typePerson === 'Juridica')
        ) {
          participanteJuridica = p
        }
        // Para de procurar se já encontrou ambos
        if (participanteFisica && participanteJuridica) break
      }

      const hasBothTypes = participanteFisica && participanteJuridica
      const hasOnlyOneParticipant = participantesList.length === 1

      // Verifica se algum participante tem saldo negativo
      const pfBalance = participanteFisica?.avaliableBalance || 0
      const pjBalance = participanteJuridica?.avaliableBalance || 0
      const hasNegativeBalanceValue = pfBalance < 0 || pjBalance < 0
      setHasNegativeBalance(hasNegativeBalanceValue)

      if (hasBothTypes) {
        // Mantém o modal aberto e mostra as opções se tiver ambos os tipos (CPF e CNPJ)
        setParticipantes(participantesList)
        setHasMultipleAccountTypes(true)
        setIsValidating(false)
        return true
      } else {
        // Fecha o modal e redireciona se tiver apenas CPF ou apenas CNPJ
        setShowModal(false)
        setPendingRedirect(false)
        setIsValidating(false)

        // Se houver apenas um participante, não envia o profileCatalogSelected
        const participante = participanteFisica || participanteJuridica
        const profileCatalogSelected = hasOnlyOneParticipant
          ? ''
          : participante?.profileCatalogSelected || ''
        openExternalLink('', profileCatalogSelected)
        return false
      }
    } catch (error) {
      // Em caso de erro, fecha o modal e redireciona
      setShowModal(false)
      setPendingRedirect(false)
      setIsValidating(false)
      openExternalLink('', '')
      return false
    }
  }, [])

  const handleSelectAccountType = useCallback(
    (accountType) => {
      setShowModal(false)
      setPendingRedirect(false)

      if (!participantes) {
        openExternalLink('', '')
        return
      }

      const participanteEscolhido = participantes.find(
        (p) =>
          (accountType === 'Physics' &&
            (p.typePerson === 'Física' ||
              p.typePerson === 'Physics' ||
              p.typePerson === 'Fisica')) ||
          (accountType === 'Legal' &&
            (p.typePerson === 'Jurídica' ||
              p.typePerson === 'Legal' ||
              p.typePerson === 'Juridica'))
      )

      // Se tiver profileCatalogSelected, usa ele, senão calcula o tipo de documento
      const profileCatalogSelected =
        participanteEscolhido?.profileCatalogSelected ||
        getDocumentType(participanteEscolhido) ||
        ''
      openExternalLink('', profileCatalogSelected)
    },
    [participantes]
  )

  const closeModal = useCallback(() => {
    setShowModal(false)
    setPendingRedirect(false)
  }, [])

  return {
    showModal,
    checkDocumentValidation,
    handleSelectAccountType,
    closeModal,
    isValidating,
    hasMultipleAccountTypes,
    hasNegativeBalance,
  }
}
